import React, { useState, useEffect, useCallback } from "react";
import {
    Alert,
    Typography,
    Box,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { format } from "date-fns";
import vitalsService from "../services/vitalsService";
import VitalsForm from "./VitalsForm";
import masterDataService from "../services/masterDataService";
import VitalsHistoryTable from "./VitalsHistoryTable";

import { formatToBackendDateTime } from "../utils/dateUtils";

interface VitalsManagerProps {
    residentId: string;
    readOnly?: boolean;
    onSaveSuccess?: () => void;
}

const VitalsManager: React.FC<VitalsManagerProps> = ({ residentId, readOnly = false, onSaveSuccess }) => {


    const [todaysVitals, setTodaysVitals] = useState({
        vitalDate: new Date() as Date | null,
        weight: "",
        temperature: "",
        pulseRate: "",
        bloodPressure: "",
        respirationRate: "",
        bloodSugar: "",
        oxygenSaturation: "",
        painLevel: "",
        consciousnessLevel: "",
        mealPortionConsumed: "",
        bowelMovement: "",
        medicationCompliance: [] as any[],
    });

    const [isSavingVitals, setIsSavingVitals] = useState(false);
    const [vitalsError, setVitalsError] = useState<string | null>(null);
    const [vitalsSuccess, setVitalsSuccess] = useState<string | null>(null);
    const [vitalsHistory, setVitalsHistory] = useState<any[]>([]);
    const [editingVitalId, setEditingVitalId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [bowelMovementOptions, setBowelMovementOptions] = useState<{ id: string; valueName: string }[]>([]);



    const fetchVitalsHistory = useCallback(async () => {
        if (!residentId) return;
        try {
            const data = await vitalsService.getVitals(String(residentId));
            let history = Array.isArray(data) ? data : [data];
            history = history.filter((v) => String(v.residentId) === String(residentId));
            history = history.map((v: any) => {
                return {
                    ...v,
                    bloodSugar: v.dailyBloodSugar || v.bloodSugar,
                    createdByName: v.createdByName || v.creatorName || "-",
                    updatedByName: v.updatedByName || v.updaterName || "-",
                };
            });
            history.sort((a: any, b: any) => new Date(b.vitalDate).getTime() - new Date(a.vitalDate).getTime());
            setVitalsHistory(history);
        } catch (e) {
            console.error("Failed to fetch vitals history", e);
        }
    }, [residentId]);

    useEffect(() => {
        fetchVitalsHistory();
    }, [fetchVitalsHistory]);

    // Keep the "Date & Time" field synchronized with the real-time system clock
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (!editingVitalId) {
            // Update immediately when conditions are met
            setTodaysVitals((prev) => ({ ...prev, vitalDate: new Date() }));
            // Tick every second to match browser time
            timer = setInterval(() => {
                setTodaysVitals((prev) => ({ ...prev, vitalDate: new Date() }));
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [editingVitalId, residentId]);

    useEffect(() => {
        const loadLookups = async () => {
            try {
                const options = await masterDataService.getActiveLookupValuesByName('BowelMovement');
                setBowelMovementOptions(options);
            } catch (err) {
                console.error('Failed to load bowel movement options', err);
            }
        };
        loadLookups();
    }, []);

    const handleSaveVitals = async () => {
        if (!residentId) {
            setVitalsError("Please select a resident before saving vitals.");
            return;
        }

        if (
            !todaysVitals.temperature ||
            !todaysVitals.pulseRate
        ) {
            setVitalsError("Please fill in all mandatory fields.");
            return;
        }

        if (todaysVitals.bloodPressure && !/^\d{2,3}\/\d{2,3}$/.test(todaysVitals.bloodPressure)) {
            setVitalsError("Blood Pressure must be in Systolic/Diastolic format (e.g. 120/80).");
            return;
        }

        setIsSavingVitals(true);
        setVitalsError(null);
        setVitalsSuccess(null);

        try {
            const payload: any = {
                residentId: residentId,
                vitalDate: formatToBackendDateTime(new Date()),
                dailyWeight: String(todaysVitals.weight || ""),
                temperature: String(todaysVitals.temperature || ""),
                pulseRate: String(todaysVitals.pulseRate || ""),
                dailyBloodPressure: todaysVitals.bloodPressure || "",
                respirationRate: String(todaysVitals.respirationRate || ""),
                dailyBloodSugar: String(todaysVitals.bloodSugar || ""),
                spO2: String(todaysVitals.oxygenSaturation || ""),
                painLevel: String(todaysVitals.painLevel || ""),
                levelOfConsciousness: todaysVitals.consciousnessLevel || "",
                mealPortion: todaysVitals.mealPortionConsumed || "",
                bowelMovement: todaysVitals.bowelMovement || "",
            };

            if (editingVitalId) {
                await vitalsService.updateVitals(editingVitalId, payload);
                setVitalsSuccess("Vitals updated successfully!");
                setEditingVitalId(null);
            } else {
                await vitalsService.createVitals(payload as any);
                setVitalsSuccess("Vitals saved successfully!");
            }

            // Reset form
            setTodaysVitals({
                vitalDate: new Date(),
                weight: "",
                temperature: "",
                pulseRate: "",
                bloodPressure: "",
                respirationRate: "",
                bloodSugar: "",
                oxygenSaturation: "",
                painLevel: "",
                consciousnessLevel: "",
                mealPortionConsumed: "",
                bowelMovement: "",
                medicationCompliance: [],
            });

            setTimeout(() => setVitalsSuccess(null), 3000);
            fetchVitalsHistory();
            if (onSaveSuccess) onSaveSuccess();
        } catch (error: any) {
            console.error("Failed to save vitals", error);
            setVitalsError(
                error.response?.data?.message ||
                error.message ||
                "Failed to save vitals. Please try again.",
            );
        } finally {
            setIsSavingVitals(false);
        }
    };

    const handleEditVital = (vital: any) => {
        setEditingVitalId(vital.id);
        setVitalsError(null);
        setTodaysVitals((prev) => ({
            ...prev,
            vitalDate: new Date(),
            bloodPressure: vital.dailyBloodPressure || vital.bloodPressure || "",
            weight: vital.dailyWeight || vital.weight || "",
            bloodSugar: vital.dailyBloodSugar || vital.bloodSugar || "",
            temperature: vital.temperature || "",
            pulseRate: vital.pulseRate || "",
            respirationRate: vital.respirationRate || "",
            oxygenSaturation: vital.spO2 || vital.oxygenSaturation || "",
            painLevel: vital.painLevel || "",
            consciousnessLevel: vital.levelOfConsciousness || vital.consciousnessLevel || "",
            mealPortionConsumed: vital.mealPortion || "",
            bowelMovement: vital.bowelMovement || "",
        }));
    };

    const cancelEditVital = () => {
        setEditingVitalId(null);
        setVitalsError(null);
        setTodaysVitals({
            vitalDate: new Date(),
            weight: "",
            temperature: "",
            pulseRate: "",
            bloodPressure: "",
            respirationRate: "",
            bloodSugar: "",
            oxygenSaturation: "",
            painLevel: "",
            consciousnessLevel: "",
            mealPortionConsumed: "",
            bowelMovement: "",
            medicationCompliance: [],
        });
    };

    return (
        <Box>
            {vitalsError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVitalsError(null)}>
                    {vitalsError}
                </Alert>
            )}

            {vitalsSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setVitalsSuccess(null)}>
                    {vitalsSuccess}
                </Alert>
            )}

            {readOnly ? (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                        Vitals History
                    </Typography>
                    <VitalsHistoryTable
                        vitalsHistory={vitalsHistory}
                        handleEditVital={handleEditVital}
                        readOnly={true}
                    />
                </>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                        Add Vitals
                    </Typography>

                    <VitalsForm
                        todaysVitals={todaysVitals}
                        setTodaysVitals={setTodaysVitals}
                        handleSaveVitals={async () => {
                            await handleSaveVitals();
                            // After saving, refetch the latest vitals so the row below
                            // always reflects the fresh data from the backend.
                            fetchVitalsHistory();
                        }}
                        isSavingVitals={isSavingVitals}
                        effectiveResidentId={residentId}
                        editingVitalId={editingVitalId}
                        cancelEditVital={cancelEditVital}
                        vitalsError={vitalsError}
                        activeTab={0}
                        latestVital={vitalsHistory[0]}
                        vitalsHistory={vitalsHistory}
                        handleEditVital={handleEditVital}
                        bowelMovementOptions={bowelMovementOptions}
                    />
                </>
            )}
        </Box>
    );
};

export default VitalsManager;